import React, { useState, useCallback, memo } from "react";
import { Row } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Login.css";
import "./reset.css";
import toast, { Toaster } from "react-hot-toast";
import instance from "../../Api";
import AppToast from "../../AppToast";

// ─── Extract reset token from URL once at module level ────────────────────────
// Safe to do here — this value never changes during the component lifetime
const urlParts    = window.location.pathname.split("/");
const RESET_TOKEN = urlParts[urlParts.length - 1];

// ─── Shared sub-components ────────────────────────────────────────────────────
const EyeToggle = memo(({ show, onToggle }) =>
  show
    ? <FaEyeSlash onClick={onToggle} style={{ color: "white", width: "20px" }} />
    : <FaEye     onClick={onToggle} style={{ color: "white", width: "20px" }} />
);

const PasswordField = memo(({ value, show, onToggle, onChange, placeholder, id }) => (
  <div style={{ marginTop: "15px", display: "flex", alignItems: "center", border: "2px solid white", borderRadius: "25px", padding: "5px" }}>
    <img src="/images/lock.svg" alt="" style={{ width: "15px", height: "15px", marginRight: "5px" }} />
    <input
      value={value ?? ""}
      type={show ? "text" : "password"}
      id={id}
      className="input-signup"
      name={id}
      placeholder={placeholder}
      style={{ color: "white", border: "none", outline: "none", background: "transparent", flex: "1", padding: "5px" }}
      onChange={(e) => onChange(e.target.value)}
    />
    <EyeToggle show={show} onToggle={onToggle} />
  </div>
));

const UpdateButton = memo(({ onClick }) => (
  <div className="log-in" style={{ marginTop: "15px", marginLeft: "-8rem", justifyContent: "center" }} onClick={onClick}>
    <a className="media-link">
      <div className="media-banner" style={{ width: "auto", height: "50px", marginTop: "10px", marginLeft: "10rem" }}>
        <img className="normal-banner"    src="/images/signup-btn-bg.png"     alt="" style={{ marginTop: "-6px", width: "12rem", height: "4rem" }} />
        <img className="hover-img-banner" src="/images/search-btn-hover.png" alt="" style={{ marginTop: "-6px", width: "12rem", height: "4rem" }} />
        <span className="login-text" style={{ fontSize: "20px", color: "#07528B", marginTop: "0px" }}>Update Password</span>
      </div>
    </a>
  </div>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const ResetPassword = ({ isMobile }) => {
  const [showPass,  setShowPass]  = useState(false);
  const [showCPass, setShowCPass] = useState(false);
  const [pass,      setPass]      = useState(null);
  const [confPass,  setConfPass]  = useState(null);

  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />),
    [isMobile]);

  const ForgotPassCall = useCallback(async () => {
    // Fix: original used `pass != ""` — but initial state is `null`, not `""`.
    // `null != ""` is true so the check ALWAYS passed even with empty fields.
    // Correct check: ensure both fields are non-null AND non-empty.
    if (!pass || !confPass) {
      showToast("Please enter password and confirm password.");
      return;
    }
    if (pass !== confPass) {
      showToast("Password and Confirm Password do not match!");
      return;
    }
    try {
      const res = await instance.post("/forgetpasswordchange", {
        token: RESET_TOKEN,
        password: pass,
        password_confirmation: confPass,
      });
      if (res.data.status) {
        showToast(res.data.message);
        window.location.href = "/login";
      }
    } catch {
      showToast("Oops! Something went wrong while fetching the data. Please try again later or contact support if the issue persists.");
    }
  }, [pass, confPass, showToast]);

  // ── Shared form — defined once, rendered in both mobile and web ───────────
  // Original duplicated the entire form JSX twice (mobile + web were identical).
  const form = (
    <div style={{ display: "grid", justifyItems: "stretch" }}>
      <PasswordField
        value={pass}
        show={showPass}
        onToggle={() => setShowPass((p) => !p)}
        onChange={setPass}
        placeholder="New Password"
        id="New Password"
      />
      <PasswordField
        value={confPass}
        show={showCPass}
        onToggle={() => setShowCPass((p) => !p)}
        onChange={setConfPass}
        placeholder="Confirm Password"
        id="Confirm Password"
      />
      <UpdateButton onClick={ForgotPassCall} />
    </div>
  );

  const cardStyle = (extraTop) => ({
    backgroundImage: `url("/images/blue-box-bg.svg")`,
    backgroundSize: "cover",
    top: extraTop,
    width: "90%",
    marginLeft: "25px",
    padding: "30px 25px",
    position: "relative",
    backgroundColor: "#07528b",
    borderRadius: "12px",
    flexWrap: "wrap",
  });

  return (
    <div style={{
      width: "100%",
      minHeight: "75rem",
      position: "relative",
      // Fix: was using an external absolute URL for a local asset — wastes bandwidth,
      // fails if the CDN is down, and is slower than a relative path.
      backgroundImage: "url(/main-bg.jpg)",
      backgroundSize: "cover",
      backgroundRepeat: "round",
      backgroundBlendMode: "overlay",
    }}>
      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      <div className="heading-dotted-changepass">Reset New Password <span /></div>

      {isMobile ? (
        // ── MOBILE ────────────────────────────────────────────────────────
        <div style={{ ...cardStyle("6rem"), height: "40vh" }}>
          {form}
        </div>
      ) : (
        // ── WEB ───────────────────────────────────────────────────────────
        <Row>
          <div className="col-md-4" />
          <div className="col-md-4">
            <div style={cardStyle("10rem")}>
              {form}
            </div>
          </div>
          <div className="col-md-4" />
        </Row>
      )}
    </div>
  );
};

export default ResetPassword;